import React from 'react';
import { Button, Card, Col, Empty, Popconfirm, Row, Tag, Tooltip, Typography } from 'antd';
import { ApiOutlined, DeleteOutlined, SyncOutlined } from '@ant-design/icons';
import { ml } from 'src/_shared/services/utils.js';
import {
	isWorkato,
	maskSecret,
	providerTitle,
	providerByValue,
	secretFieldNames,
	syncEndpointFor,
} from './integrationCatalog.js';

const { Text } = Typography;

/** Entry attributes that are metadata rather than credentials. */
const HIDDEN_FIELDS = new Set([
	'ATSName',
	'ATSHiringManager',
	'ATSBonusBuilder',
	'ATSNotificationType',
	'ATSCreatedById',
]);

const NOTIFICATION_LABELS = {
	ALL: 'Notifies all employees',
	DEPARTMENT: 'Notifies department only',
	NONE: 'No notifications',
};

const detailRows = (ats) =>
	Object.entries(ats)
		.filter(([key, value]) => !HIDDEN_FIELDS.has(key) && value !== null && value !== '')
		.map(([key, value]) => ({
			key,
			value:
				typeof value === 'object'
					? JSON.stringify(value)
					: secretFieldNames.has(key)
						? maskSecret(value)
						: String(value),
			secret: secretFieldNames.has(key),
		}));

const IntegrationCard = ({
	ats,
	currentUser,
	allMultiLingualData,
	onRemove,
	onSync,
	syncDisabled,
	onImportDepartments,
}) => {
	const provider = providerByValue(ats.ATSName);
	const syncEndpoint = syncEndpointFor(ats.ATSName);
	const rows = detailRows(ats);

	return (
		<Card
			size="small"
			style={{ height: '100%' }}
			title={
				<span>
					<ApiOutlined style={{ marginRight: 8, color: '#1890ff' }} />
					{providerTitle(ats.ATSName)}
					<Tag style={{ marginLeft: 8 }} color={isWorkato(ats.ATSName) ? 'geekblue' : 'default'}>
						{isWorkato(ats.ATSName) ? 'Managed' : 'Direct'}
					</Tag>
				</span>
			}
			extra={
				<Popconfirm
					title={ml(
						'Remove this integration?',
						currentUser,
						allMultiLingualData
					)}
					okText={ml('Remove', currentUser, allMultiLingualData)}
					okButtonProps={{ danger: true }}
					cancelText={ml('Cancel', currentUser, allMultiLingualData)}
					onConfirm={() => onRemove(ats)}
				>
					<Button type="text" danger size="small" icon={<DeleteOutlined />} />
				</Popconfirm>
			}
		>
			<div style={{ minHeight: 48 }}>
				{rows.length === 0 ? (
					<Text type="secondary" style={{ fontSize: 12 }}>
						No stored credentials.
					</Text>
				) : (
					rows.map((row) => (
						<div key={row.key} style={{ fontSize: 12, lineHeight: '20px' }}>
							<Text type="secondary">{row.key}: </Text>
							<Text code={row.secret}>{row.value}</Text>
						</div>
					))
				)}
				{ats.ATSHiringManager?.name ? (
					<div style={{ fontSize: 12, lineHeight: '20px' }}>
						<Text type="secondary">Hiring contact: </Text>
						<Text>{ats.ATSHiringManager.name}</Text>
					</div>
				) : null}
				{ats.ATSBonusBuilder?.name ? (
					<div style={{ fontSize: 12, lineHeight: '20px' }}>
						<Text type="secondary">Referral bonus: </Text>
						<Text>{ats.ATSBonusBuilder.name}</Text>
					</div>
				) : null}
				{ats.ATSNotificationType ? (
					<div style={{ fontSize: 12, lineHeight: '20px' }}>
						<Text type="secondary">
							{NOTIFICATION_LABELS[ats.ATSNotificationType] || ats.ATSNotificationType}
						</Text>
					</div>
				) : null}
			</div>
			{syncEndpoint || provider?.supportsDepartmentImport ? (
				<div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
					{syncEndpoint ? (
						<Tooltip title="Start a job sync now. Allow up to 15 minutes.">
							<Button
								size="small"
								icon={<SyncOutlined />}
								disabled={syncDisabled}
								onClick={() => onSync(ats)}
							>
								{ml('Sync Jobs', currentUser, allMultiLingualData)}
							</Button>
						</Tooltip>
					) : null}
					{provider?.supportsDepartmentImport ? (
						<Button size="small" onClick={() => onImportDepartments(ats)}>
							{ml('Import Departments', currentUser, allMultiLingualData)}
						</Button>
					) : null}
				</div>
			) : null}
		</Card>
	);
};

const ConnectedIntegrations = ({
	integrations,
	currentUser,
	allMultiLingualData,
	onRemove,
	onSync,
	syncDisabledFor,
	onImportDepartments,
}) => {
	if (!integrations || integrations.length === 0) {
		return (
			<Empty
				image={Empty.PRESENTED_IMAGE_SIMPLE}
				description={ml(
					'No ATS integrations connected yet.',
					currentUser,
					allMultiLingualData
				)}
			/>
		);
	}

	return (
		<Row gutter={[12, 12]}>
			{integrations.map((ats, index) => (
				<Col key={`${ats.ATSName}-${index}`} xs={24} md={12} xl={8}>
					<IntegrationCard
						ats={ats}
						currentUser={currentUser}
						allMultiLingualData={allMultiLingualData}
						onRemove={onRemove}
						onSync={onSync}
						syncDisabled={syncDisabledFor(ats)}
						onImportDepartments={onImportDepartments}
					/>
				</Col>
			))}
		</Row>
	);
};

export default ConnectedIntegrations;
